import { Check, Edit, PlusCircle, Trash, X } from 'lucide-react';
import { useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useProductSystem } from '../../hooks/useProductSystem';

export function ProductTags() {
  const { chatbotPublicId } = useChatbot();
  const { tags, addTag, updateTag, deleteTag, loading } = useProductSystem();
  const [newTag, setNewTag] = useState('');
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const handleAddTag = async () => {
    if (!newTag.trim() || !chatbotPublicId) return;
    
    try {
      await addTag(chatbotPublicId, newTag);
      setNewTag('');
    } catch (error) {
      console.error('Error adding tag:', error);
    }
  };

  const handleUpdateTag = async (tagId: string) => {
    if (!editName.trim() || !chatbotPublicId) return;
    
    try {
      await updateTag(chatbotPublicId, tagId, editName);
      setEditingTag(null);
    } catch (error) {
      console.error('Error updating tag:', error);
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    if (!chatbotPublicId) return;
    
    try {
      await deleteTag(chatbotPublicId, tagId);
    } catch (error) {
      console.error('Error deleting tag:', error);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={(e) => {
        e.preventDefault();
        handleAddTag();
      }} className="flex gap-2">
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Nouveau tag"
        />
        <button
          type="submit"
          disabled={loading || !newTag.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
        >
          <PlusCircle className="w-4 h-4" />
          Add
        </button>
      </form>

      <div className="flex flex-wrap gap-2">
        {tags.map(tag => (
          <div
            key={tag.id}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10"
          >
            {editingTag === tag.id ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleUpdateTag(tag.id);
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="text"
                  name="name"
                  defaultValue={tag.name}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-24 bg-white/5 border border-white/10 rounded px-2 py-0.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  autoFocus
                />
                <button
                  type="submit"
                  className="p-1 text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setEditingTag(null)}
                  className="p-1 text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </form>
            ) : (
              <>
                <span className="text-white">{tag.name}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setEditingTag(tag.id)}
                    className="p-1 text-gray-400 hover:text-white transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteTag(tag.id)}
                    className="p-1 text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}